import {
    Alert,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
} from '@mui/material';
import React, { useEffect } from 'react';
import { useTranslation } from '../../../hooks/useTranslation';
import PasswordStrengthIndicator from '../../auth/PasswordStrengthIndicator';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { PASSWORD_MIN_LENGTH, PASSWORD_REGEX } from '../../../utils/validation/auth.schema';
import FormTextField from '../../auth/FormTextField';

// Create a schema for the change password form
const changePasswordSchema = z
    .object({
        currentPassword: z.string().min(1, { message: 'auth.validation.required' }),
        newPassword: z
            .string()
            .min(PASSWORD_MIN_LENGTH, { message: 'auth.validation.passwordTooShort' })
            .regex(PASSWORD_REGEX, { message: 'auth.validation.passwordComplexity' }),
        confirmPassword: z.string().min(1, { message: 'auth.validation.required' }),
    })
    .refine((data) => data.newPassword === data.confirmPassword, {
        message: 'auth.validation.passwordsDontMatch',
        path: ['confirmPassword'],
    });

type ChangePasswordFormData = z.infer<typeof changePasswordSchema>;

interface ChangePasswordDialogProps {
    open: boolean;
    isLoading: boolean;
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
    onClose: () => void;
    onConfirm: () => void;
    onCurrentPasswordChange: (value: string) => void;
    onNewPasswordChange: (value: string) => void;
    onConfirmPasswordChange: (value: string) => void;
}

const ChangePasswordDialog: React.FC<ChangePasswordDialogProps> = ({
    open,
    isLoading,
    currentPassword,
    newPassword,
    confirmPassword,
    onClose,
    onConfirm,
    onCurrentPasswordChange,
    onNewPasswordChange,
    onConfirmPasswordChange,
}) => {
    const { t } = useTranslation();
    
    // Errors are now handled directly in the SettingsPage component using NotificationContext
    
    const {
        control,
        handleSubmit,
        watch,
        formState: { touchedFields, isValid, isDirty },
        trigger,
    } = useForm<ChangePasswordFormData>({
        resolver: zodResolver(changePasswordSchema),
        defaultValues: {
            currentPassword: currentPassword,
            newPassword: newPassword,
            confirmPassword: confirmPassword,
        },
        mode: 'onChange', // Changed from 'onBlur' to 'onChange' to validate as user types
    });

    // Watch password for strength indicator and update parent state
    const watchedPassword = watch('newPassword');
    
    // Update parent component state when form values change
    useEffect(() => {
        // Update parent state with form values
        const subscription = watch((value) => {
            if (value.currentPassword !== undefined) {
                onCurrentPasswordChange(value.currentPassword);
            }
            if (value.newPassword !== undefined) {
                onNewPasswordChange(value.newPassword);
            }
            if (value.confirmPassword !== undefined) {
                onConfirmPasswordChange(value.confirmPassword);
            }
        });
        
        // Cleanup subscription on unmount
        return () => subscription.unsubscribe();
    }, [watch, onCurrentPasswordChange, onNewPasswordChange, onConfirmPasswordChange]);
    
    // Only trigger validation after user has interacted with fields
    useEffect(() => {
        if (touchedFields.newPassword || touchedFields.confirmPassword || touchedFields.currentPassword) {
            trigger();
        }
    }, [trigger, touchedFields]);
    
    const onSubmit = () => {
        onConfirm();
    };
    
    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            BackdropProps={{
                sx: {
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    backdropFilter: 'blur(5px)'
                }
            }}
        >
            <DialogTitle>{t('settings.dialogs.changePassword.title')}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ 
                    mt: 1, 
                    gap: 2 
                }}>
                    <form onSubmit={handleSubmit(onSubmit)} noValidate>
                        <Stack spacing={2}>
                            <FormTextField
                                name="currentPassword"
                                control={control}
                                type="password"
                                label={t('settings.dialogs.changePassword.currentPassword')}
                                fullWidth
                                showPasswordToggle
                                showErrorsOnlyWhenTouched={false} // Show errors even when not touched
                                sx={{ mb: 2 }}
                                disabled={isLoading}
                            />
                            
                            <FormTextField
                                name="newPassword"
                                control={control}
                                type="password"
                                label={t('settings.dialogs.changePassword.newPassword')}
                                fullWidth
                                showPasswordToggle
                                showErrorsOnlyWhenTouched={false} // Show errors even when not touched
                                sx={{ mb: 1 }}
                                disabled={isLoading}
                            />
                            
                            <PasswordStrengthIndicator password={watchedPassword} />
                            
                            <FormTextField
                                name="confirmPassword"
                                control={control}
                                type="password"
                                label={t('settings.dialogs.changePassword.confirmPassword')}
                                fullWidth
                                showPasswordToggle
                                showErrorsOnlyWhenTouched={false} // Show errors even when not touched
                                sx={{ mt: 2 }}
                                disabled={isLoading}
                            />
                        </Stack>
                    </form>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('settings.dialogs.changePassword.buttons.cancel')}</Button>
                <Button
                    onClick={handleSubmit(onSubmit)}
                    color="primary"
                    disabled={!isValid || !isDirty || isLoading}
                >
                    {isLoading ? <CircularProgress size={24} /> : t('settings.dialogs.changePassword.buttons.update')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ChangePasswordDialog;
